import type { Edge, Node } from '@xyflow/react';
import i18next from '../i18n/initI18n';
import { linearizeVisualChannelSteps } from '../components/mailing-constructor/visualChainLinearize';
import { visualNodesAllChannelsReady, visualSmsViberDelayBetweenChannelsReady } from '../components/mailing-constructor/visualFlowReadiness';
import { normalizeStartNodeData, normalizeChannelNodeData, startScheduleIsPast, visualChainIsEventTrigger } from '../components/mailing-constructor/mailingTypes';

export type MailingChannel = 'push' | 'sms' | 'email' | 'viber' | 'waba';

export type ScenarioStep = {
  channel: MailingChannel;
  order: number;
  /** Задержка перед следующим шагом после недоставки (мин) */
  delayMinutes: number;
  /** Ожидание статуса доставки (мин) */
  timeoutMinutes: number;
};

export type Scenario = {
  id: string;
  name: string;
  steps: ScenarioStep[];
  locked: boolean;
  createdAt: number;
  updatedAt: number;
  /** Если сценарий создан из визуальной цепочки — тот же id, что в /api/visual-mailings */
  visualMailingId?: string | null;
};

export type ChannelContent = {
  title: string;
  body: string;
  /** Параметры отправки настроены (чекбокс) */
  sendingConfigured: boolean;
  /** Push: ссылка на изображение */
  imageUrl?: string;
  /** Push: URL при нажатии */
  actionUrl?: string;
  /** SMS: подпись / альфа-имя */
  senderName?: string;
  /** Email: прехедер */
  preheader?: string;
  /** Viber: подпись кнопки */
  buttonText?: string;
  /** Viber: URL кнопки */
  buttonUrl?: string;
  /** WABA: имя шаблона у провайдера */
  templateName?: string;
  /** WABA: код языка шаблона */
  languageCode?: string;
};

export type CampaignContent = Partial<Record<MailingChannel, ChannelContent>>;

export type AudienceUser = {
  userId: string;
  phone?: string;
  email?: string;
  pushToken?: string;
  viberId?: string;
  wabaId?: string;
};

/** Как задаётся аудитория в UI кампании. */
export type AudienceInputMode = 'phones' | 'api';

/** Построчно: телефон (≥10 цифр) или произвольный userId. */
export function audienceUsersFromPhoneLines(text: string): AudienceUser[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const out: AudienceUser[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const digits = line.replace(/\D/g, '');
    if (digits.length >= 10) {
      const phoneNorm = `+${digits}`;
      out.push({ userId: `u_${digits}_${i}`, phone: phoneNorm });
    } else {
      out.push({ userId: line });
    }
  }
  return out;
}

export function audienceLinesFromUsers(users: AudienceUser[]): string {
  return users
    .map((u) => {
      const p = u.phone != null && String(u.phone).trim() !== '' ? String(u.phone).trim() : '';
      return p || u.userId;
    })
    .join('\n');
}

/** Доп. поля кампании (UI конструктора); движок может игнорировать. */
export type CampaignSettings = {
  /** Webhook POST при завершении цепочки по пользователю (опционально). */
  notifyWebhookUrl?: string;
  /** Визуальная цепочка с сервера (id из /api/visual-mailings). */
  visualMailingId?: string | null;
  /** Режим ввода аудитории в форме кампании. */
  audienceInputMode?: AudienceInputMode;
  /** GET/POST URL: список получателей подгружается при запуске (на стороне сервера). */
  audienceApiUrl?: string;
  /** Несколько списков контактов SMS-сервиса (contactListId). Порядок и состав фиксируются после первого сохранения. */
  subscriberContactListIds?: string[] | null;
  /** Одна цепочка: старые данные с одним списком (читается, если массив пуст). */
  subscriberContactListId?: string | null;
  /** Ошибка фоновой активации триггерной кампании (кэш контактов / API-ключ). */
  eventTriggerActivationError?: string | null;
  /** После рестарта сервиса: ждём JWT с poll/activate для продолжения activating. */
  eventTriggerActivationNeedsResume?: boolean | null;
  /** @deprecated исполнение только по визуальному графу; флаг сохранён для совместимости со старыми кампаниями */
  useVisualGraphRuntime?: boolean;
};

/** Ответ каскада vue-sms после create; confirm — после кнопки «Подтвердить» у нас. */
export type CampaignProviderSubmit = {
  httpStatus?: number;
  body?: unknown;
  messageId?: string;
  confirmKind?: 'viber' | 'sms' | 'email' | 'sms_viber';
  /** true — ждём POST /provider-confirm от UI (как модалка vue-sms). */
  confirmPending?: boolean;
  confirm?: { httpStatus?: number; body?: unknown };
  cancel?: { httpStatus?: number; body?: unknown };
};

export type Campaign = {
  id: string;
  name: string;
  /** Серверный сценарий только по привязанной визуальной цепочке (`visualMailingId`). Поле `scenarioId` на кампании не используется. */
  scenarioId: string | null;
  audience: AudienceUser[];
  content: CampaignContent;
  settings?: CampaignSettings;
  status: 'draft' | 'running' | 'completed' | 'stopped' | 'aborted' | 'active' | 'activating' | 'paused';
  locked: boolean;
  scenarioSnapshot: { id: string; name: string; steps: ScenarioStep[] } | null;
  createdAt: number;
  updatedAt: number;
  providerSubmit?: CampaignProviderSubmit;
  /** Скрыта из основного списка; запуск по-прежнему через «Архив» или после возврата. */
  archived?: boolean;
};

/** Единое чтение с сервера: старые кампании без поля считаются не в архиве. */
export function normalizeCampaignFromApi(campaign: Campaign): Campaign {
  return { ...campaign, archived: Boolean(campaign.archived) };
}

export type ExecutionLog = {
  id: string;
  timestamp: number;
  executionId: string;
  userId: string;
  campaignId: string;
  scenarioId: string;
  stepIndex: number;
  channel: string;
  status: 'delivered' | 'not_delivered' | null;
  event: string;
  detail: string;
  visualNodeId?: string | null;
  dryRun?: boolean;
};

export function sortSteps<T extends { order: number }>(steps: T[]): T[] {
  return [...steps].sort((a, b) => a.order - b.order);
}

export function channelContentReady(content: CampaignContent, channel: MailingChannel): boolean {
  const c = content[channel];
  if (!c) return false;
  const body = String(c.body || '').trim();
  if (!body || !c.sendingConfigured) return false;
  if (channel === 'push' || channel === 'email') {
    return Boolean(String(c.title || '').trim());
  }
  return true;
}

export function channelsRequiredByScenario(steps: ScenarioStep[]): MailingChannel[] {
  const seen = new Set<MailingChannel>();
  const out: MailingChannel[] = [];
  for (const s of sortSteps(steps)) {
    if (!seen.has(s.channel)) {
      seen.add(s.channel);
      out.push(s.channel);
    }
  }
  return out;
}

export type CampaignLaunchOpts = {
  /** Каналы из узлов визуальной цепочки (если уже загружена). */
  visualGraphChannels?: MailingChannel[];
  /** Узлы/рёбра цепочки — если переданы, готовность каналов считается по блокам, а не по campaign.content. */
  visualNodes?: Node[];
  visualEdges?: Edge[];
  /** Списки контактов кампании для готовности SMS/Viber/Email в узлах. */
  subscriberContactListIds?: string[] | null;
  /** Совместимость: первый список, если многож не передали. */
  subscriberContactListId?: string | null;
};

function channelsFromVisualNodes(nodes: Array<{ type?: string; data?: unknown }>): MailingChannel[] {
  const set = new Set<MailingChannel>();
  for (const n of nodes) {
    if (n.type === 'mailingChannel') {
      const d = n.data as { channel?: MailingChannel };
      if (d?.channel && ['push', 'sms', 'email', 'viber', 'waba'].includes(d.channel)) {
        set.add(d.channel);
      }
    }
  }
  return Array.from(set);
}

export function campaignChannelsFromVisualNodes(
  nodes: Array<{ type?: string; data?: unknown }> | undefined
): MailingChannel[] {
  if (!nodes?.length) return [];
  return channelsFromVisualNodes(nodes);
}

export function resolveScenarioForCampaign(campaign: Campaign, scenarios: Scenario[]): Scenario | undefined {
  const vid = campaign.settings?.visualMailingId;
  if (!vid || !String(vid).trim()) return undefined;
  return scenarios.find((s) => s.visualMailingId === vid);
}

/** Нельзя отвязать или сменить привязанную цепочку (черновик и «на паузе» — можно править). */
export function campaignChainAttachmentLocked(campaign: Campaign | undefined | null): boolean {
  if (!campaign) return false;
  if (campaign.locked) return true;
  const s = campaign.status;
  if (s === 'draft' || s === 'paused') return false;
  return true;
}

/** Кампании, у которых привязана эта визуальная цепочка (по settings.visualMailingId). */
export function campaignNamesLinkedToVisualChain(
  visualMailingId: string,
  campaigns: Campaign[],
  _scenarios: Scenario[]
): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const c of campaigns) {
    if (seen.has(c.id)) continue;
    let linked = false;
    if (c.settings?.visualMailingId === visualMailingId) linked = true;
    if (linked) {
      seen.add(c.id);
      names.push(c.name);
    }
  }
  const loc = i18next.language?.startsWith('en') ? 'en' : 'ru';
  return names.sort((a, b) => a.localeCompare(b, loc));
}

/** Цепочку нельзя удалить или менять граф: есть привязка к кампании не в черновике. */
export function visualChainLockedByLaunchedCampaign(visualMailingId: string, campaigns: Campaign[]): boolean {
  const vid = String(visualMailingId || '').trim();
  if (!vid) return false;
  return campaigns.some(
    (c) => String(c.settings?.visualMailingId ?? '').trim() === vid && campaignChainAttachmentLocked(c)
  );
}

/** Цепочка занята другой кампанией после запуска — нельзя привязать к `forCampaignId`. */
export function visualChainLockedToOtherLaunchedCampaign(
  visualMailingId: string,
  forCampaignId: string,
  campaigns: Campaign[]
): boolean {
  const vid = String(visualMailingId || '').trim();
  if (!vid) return false;
  return campaigns.some(
    (c) =>
      c.id !== forCampaignId &&
      String(c.settings?.visualMailingId ?? '').trim() === vid &&
      campaignChainAttachmentLocked(c)
  );
}

/** Нормализованные contactListId: сначала из opts запуска, затем из настроек кампании (массив или legacy одно поле). */
export function normalizeSubscriberContactListIds(
  settings?: CampaignSettings | null,
  launch?: Pick<CampaignLaunchOpts, 'subscriberContactListIds' | 'subscriberContactListId'> | null
): string[] {
  const pushUnique = (acc: string[], raw: unknown) => {
    const s = String(raw ?? '').trim();
    if (!s || acc.includes(s)) return;
    acc.push(s);
  };

  const fromArr = (arr: unknown, acc: string[]) => {
    if (!Array.isArray(arr)) return;
    for (const x of arr) pushUnique(acc, x);
  };

  if (launch) {
    const acc: string[] = [];
    fromArr(launch.subscriberContactListIds, acc);
    if (acc.length) return acc;
    if (launch.subscriberContactListId !== undefined && launch.subscriberContactListId !== null) {
      const one = String(launch.subscriberContactListId).trim();
      if (one) return [one];
    }
  }

  const acc: string[] = [];
  fromArr(settings?.subscriberContactListIds, acc);
  if (acc.length) return acc;
  if (settings?.subscriberContactListId !== undefined && settings?.subscriberContactListId !== null) {
    const one = String(settings.subscriberContactListId).trim();
    if (one) return [one];
  }
  return [];
}

function subscriberContactListIdsMultisetKey(ids: string[]): string {
  return [...ids].sort((a, b) => a.localeCompare(b, 'en')).join('\0');
}

/**
 * Списки контактов из кампаний, привязанных к визуальной цепочке: один набор на все привязки или null при конфликте/отсутствии.
 */
export function resolveSubscriberListIdsForVisualChain(visualMailingId: string, campaigns: Campaign[]): string[] | null {
  const vid = String(visualMailingId || '').trim();
  if (!vid) return null;
  const loc = i18next.language?.startsWith('en') ? 'en' : 'ru';
  const linked = campaigns
    .filter((c) => String(c.settings?.visualMailingId ?? '').trim() === vid)
    .sort((a, b) => a.id.localeCompare(b.id, loc));
  if (!linked.length) return null;

  const perCampaign = linked.map((c) => normalizeSubscriberContactListIds(c.settings));
  const nonEmpty = perCampaign.filter((a) => a.length > 0);
  if (nonEmpty.length === 0) return null;
  if (nonEmpty.length < perCampaign.length) return null;

  const k0 = subscriberContactListIdsMultisetKey(nonEmpty[0]!);
  for (let i = 1; i < nonEmpty.length; i++) {
    if (subscriberContactListIdsMultisetKey(nonEmpty[i]!) !== k0) return null;
  }
  return nonEmpty[0]!;
}

/** Первый список при согласованных привязках; иначе null (для обратной совместимости). */
export function resolveSubscriberListIdForVisualChain(visualMailingId: string, campaigns: Campaign[]): string | null {
  const ids = resolveSubscriberListIdsForVisualChain(visualMailingId, campaigns);
  return ids?.[0] ?? null;
}

export function campaignCanLaunch(
  campaign: Campaign,
  _scenario: Scenario | null | undefined,
  opts?: CampaignLaunchOpts
): { ok: boolean; reason?: string } {
  if (campaign.status === 'aborted') return { ok: false, reason: i18next.t('domain.campaignAbortedCabinet') };
  const vmId = String(campaign.settings?.visualMailingId ?? '').trim();
  if (!vmId) return { ok: false, reason: i18next.t('domain.selectVisualChain') };

  const vNodes = opts?.visualNodes;
  if (vNodes?.length && visualChainIsEventTrigger(vNodes)) {
    return { ok: false, reason: i18next.t('domain.useEventActivate') };
  }

  const chs = opts?.visualGraphChannels ?? [];
  if (chs.length === 0) return { ok: false, reason: i18next.t('domain.loadVisualChain') };

  const vEdges = opts?.visualEdges;
  const listIds = normalizeSubscriberContactListIds(campaign.settings, opts);
  const listOpt = listIds[0] ?? null;
  if (vNodes?.length && vEdges && Array.isArray(vEdges)) {
    const startNode = vNodes.find((n) => n.type === 'mailingStart');
    const startD = startNode ? normalizeStartNodeData(startNode.data) : null;
    if (startD && startScheduleIsPast(startD.scheduleDate, startD.scheduleTime)) {
      return { ok: false, reason: i18next.t('domain.startScheduleInPast') };
    }
    const readinessOpts =
      listIds.length || startD
        ? {
            ...(listIds.length ? { subscriberContactListIds: listIds, subscriberContactListId: listOpt } : {}),
            ...(startD
              ? {
                  startScheduleDate: startD.scheduleDate ?? '',
                  startScheduleTime: startD.scheduleTime ?? '',
                }
              : {}),
          }
        : undefined;

    const vr = visualNodesAllChannelsReady(vNodes, vEdges, chs, readinessOpts);
    if (!vr.ok) {
      return {
        ok: false,
        reason: i18next.t('domain.channelNotReadyVisual', {
          channel: i18next.t(`channels.${vr.failingChannel}`),
        }),
      };
    }
    const chsOrdered = linearizeVisualChannelSteps(vNodes, vEdges).map((s) => s.channel);
    if (!visualSmsViberDelayBetweenChannelsReady(vNodes, vEdges, chsOrdered)) {
      return { ok: false, reason: i18next.t('domain.cascadeNeedsDelayBetweenChannels') };
    }
    return { ok: true };
  }

  for (const ch of chs) {
    if (!channelContentReady(campaign.content || {}, ch)) {
      return {
        ok: false,
        reason: i18next.t('domain.channelNotReadyVisual', { channel: i18next.t(`channels.${ch}`) }),
      };
    }
  }
  return { ok: true };
}

export function campaignCanActivateEvent(
  campaign: Campaign,
  opts?: CampaignLaunchOpts
): { ok: boolean; reason?: string } {
  if (campaign.status === 'aborted') return { ok: false, reason: i18next.t('domain.campaignAbortedCabinet') };
  if (campaign.status === 'active') return { ok: false, reason: i18next.t('domain.eventAlreadyActive') };
  if (campaign.status === 'activating') return { ok: false, reason: i18next.t('domain.eventActivating') };
  const vmId = String(campaign.settings?.visualMailingId ?? '').trim();
  if (!vmId) return { ok: false, reason: i18next.t('domain.selectVisualChain') };
  const vNodes = opts?.visualNodes;
  const vEdges = opts?.visualEdges;
  if (!vNodes?.length || !vEdges) return { ok: false, reason: i18next.t('domain.loadVisualChain') };
  if (!visualChainIsEventTrigger(vNodes)) {
    return { ok: false, reason: i18next.t('domain.notEventChain') };
  }
  const startNode = vNodes.find((n) => n.type === 'mailingStart');
  const startD = startNode ? normalizeStartNodeData(startNode.data) : null;
  if (!startD?.eventFieldKey?.trim()) {
    return { ok: false, reason: i18next.t('domain.eventFieldRequired') };
  }
  if (!startD.scheduleTime?.trim()) {
    return { ok: false, reason: i18next.t('domain.eventSendTimeRequired') };
  }
  const listIds = normalizeSubscriberContactListIds(campaign.settings, opts);
  if (!listIds.length) return { ok: false, reason: i18next.t('domain.needSubscriberList') };

  const chs = opts?.visualGraphChannels ?? [];
  const listOpt = listIds[0] ?? null;
  const readinessOpts = {
    subscriberContactListIds: listIds,
    subscriberContactListId: listOpt,
    isEventChain: true,
  };
  const vr = visualNodesAllChannelsReady(vNodes, vEdges, chs, readinessOpts);
  if (!vr.ok) {
    return {
      ok: false,
      reason: i18next.t('domain.channelNotReadyVisual', {
        channel: i18next.t(`channels.${vr.failingChannel}`),
      }),
    };
  }

  const offsets: number[] = [];
  for (const n of vNodes) {
    if (n.type !== 'mailingChannel') continue;
    const d = normalizeChannelNodeData(n.data);
    if (d.channel !== 'sms' && d.channel !== 'viber' && d.channel !== 'email') continue;
    const raw = String(d.triggerOffsetDays ?? '').trim();
    const off = Number(raw);
    if (!raw || !Number.isFinite(off) || off < 0) {
      return { ok: false, reason: i18next.t('domain.triggerOffsetRequired') };
    }
    offsets.push(off);
  }
  if (!offsets.length) return { ok: false, reason: i18next.t('domain.eventChannelStepRequired') };
  return { ok: true };
}

/** Состояние кампании для подписи в списке (частично по ответу провайдера). */
export type CampaignDisplayStatus =
  | 'draft'
  | 'running'
  | 'queued'
  | 'delivered'
  | 'stopped'
  | 'cancelled';

function parseLooseDateTimeToMs(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const n = raw;
    return n < 1e12 ? n * 1000 : n;
  }
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  if (!t) return null;
  const iso = Date.parse(t);
  if (!Number.isNaN(iso)) return iso;
  const dm = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (dm) {
    const dd = Number(dm[1]);
    const mm = Number(dm[2]);
    const yyyy = Number(dm[3]);
    const hh = dm[4] !== undefined ? Number(dm[4]) : 0;
    const min = dm[5] !== undefined ? Number(dm[5]) : 0;
    const ss = dm[6] !== undefined ? Number(dm[6]) : 0;
    const d = new Date(yyyy, mm - 1, dd, hh, min, ss, 0);
    const ms = d.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

function mergeScheduleDateTimeStrings(dateStr: string, timeStr: string): number | null {
  const datePart = dateStr.trim();
  const timePart = timeStr.trim();
  if (!datePart) return null;
  if (!timePart) return parseLooseDateTimeToMs(datePart);
  return parseLooseDateTimeToMs(`${datePart} ${timePart}`);
}

function extractProviderScheduledSendEpochMs(ps: CampaignProviderSubmit | undefined): number | null {
  if (!ps) return null;
  let best: number | null = null;
  const bump = (ms: number | null) => {
    if (ms === null || Number.isNaN(ms)) return;
    if (best === null || ms < best) best = ms;
  };

  const scan = (node: unknown): void => {
    if (node === null || node === undefined) return;
    if (typeof node !== 'object') return;
    if (Array.isArray(node)) {
      for (const x of node) scan(x);
      return;
    }
    const o = node as Record<string, unknown>;
    const single =
      o.scheduled ?? o.scheduledAt ?? o.sendAt ?? o.plannedAt ?? o.plannedSendAt ?? o.mailingScheduled;
    bump(parseLooseDateTimeToMs(single));

    const sd = o.scheduleDate ?? o.schedule_date ?? o.scheduledDate;
    const st = o.scheduleTime ?? o.schedule_time ?? o.scheduledTime;
    if (sd != null && st != null) {
      bump(mergeScheduleDateTimeStrings(String(sd), String(st)));
    }
    for (const v of Object.values(o)) scan(v);
  };

  scan(ps.body);
  scan(ps.confirm?.body);
  return best;
}

/**
 * Как показывать статус в UI: «В очереди», если есть расписание в ответе провайдера и время ещё не наступило.
 * «Отменена» и «Доставлено» — через aborted / completed.
 */
export function campaignDisplayStatus(
  campaign: Campaign,
  nowMs: number = Date.now()
): CampaignDisplayStatus {
  if (campaign.status === 'aborted') return 'cancelled';
  if (campaign.status === 'draft') return 'draft';
  if (campaign.status === 'paused') return 'stopped';
  if (campaign.status === 'activating') return 'queued';
  if (campaign.status === 'active') return 'running';
  if (campaign.status === 'stopped') return 'stopped';
  if (campaign.status === 'completed') return 'delivered';

  if (campaign.status === 'running') {
    const ts = extractProviderScheduledSendEpochMs(campaign.providerSubmit);
    if (ts !== null && nowMs < ts) return 'queued';
    return 'running';
  }

  return 'draft';
}
